/**
 * Права доступа в админку.
 *
 * Источник правды — роли на сервере Discord: отдельных учётных записей нет,
 * и снять доступ можно, просто сняв роль. Уровень пересчитывается при каждом
 * входе и периодически обновляется у активных сессий, поэтому разжалованный
 * модератор теряет доступ без ручных действий.
 */

import type { Snowflake } from './domain.js';

/**
 * Уровень доступа, выведенный из ролей Discord.
 *
 * `player` — рядовой игрок семьи с ролью tier 1–3 или plus. Он не управляет ничем и
 * заведён ради одного: показать сообществу его же статистику каптов, не
 * раздавая при этом права модератора.
 */
export type AccessLevel = 'player' | 'moderator' | 'admin' | 'owner';

/** Отдельное разрешение на действие. */
export type Capability =
  | 'events.view'
  | 'events.create'
  | 'events.manage'
  | 'events.cancel'
  /** Удаление набора насовсем — отдельно от отмены: отмену можно откатить. */
  | 'events.delete'
  | 'members.manage'
  | 'applications.review'
  /**
   * Статистика каптов: сводка, лента боёв, страница боя, карточка игрока.
   *
   * Отдельно от `players.view`: база игроков — рабочий инструмент модерации
   * (правка анкет, отбор, выгрузка), а статистика — то, что смотрит вся
   * семья. Одним разрешением их не покрыть, не отдав игрокам базу.
   */
  | 'stats.view'
  | 'players.view'
  | 'players.edit'
  /** Удаление анкеты из базы — отдельно от правки: правку можно исправить. */
  | 'players.delete'
  /**
   * Правка ролей Discord у человека: смена тира и снятие всех ролей разом.
   * Отдельно от правки анкеты: там ник, здесь доступ ко всему серверу.
   * Модератору оставлено: журнал хранит и снятое, и прежний тир, поэтому
   * ошибку видно и можно отыграть назад.
   */
  | 'players.roles'
  | 'rollbacks.view'
  /**
   * Дефолты: карты каптов и разметка тактики поверх них.
   *
   * Одно разрешение и на просмотр, и на правку: вкладка — рабочий стол
   * модерации, а не витрина. Игроку она закрыта целиком, поэтому делить
   * её на «смотреть» и «менять» нечего.
   */
  | 'tactics.manage'
  | 'audit.view'
  | 'settings.edit';

/** Полный доступ: у администратора (`morethenmod`) и владельца он одинаковый. */
const FULL_CAPABILITIES: readonly Capability[] = [
  'events.view',
  'events.create',
  'events.manage',
  'events.cancel',
  'events.delete',
  'members.manage',
  'applications.review',
  'stats.view',
  'players.view',
  'players.edit',
  'players.delete',
  'players.roles',
  'rollbacks.view',
  'tactics.manage',
  'audit.view',
  'settings.edit',
];

/**
 * Модератор (`mentalcore`) работает почти со всем, кроме необратимого
 * и настроек: удалить набор или анкету он не может — это не откатывается,
 * а настройки задают правила игры для всего сообщества.
 */
const MODERATOR_DENIED: readonly Capability[] = ['events.delete', 'players.delete', 'settings.edit'];

const MODERATOR_CAPABILITIES: readonly Capability[] = FULL_CAPABILITIES.filter(
  (capability) => !MODERATOR_DENIED.includes(capability),
);

/**
 * Игрок видит статистику и ничего больше.
 *
 * Список из одного разрешения — это намеренно: всё, что появится в админке
 * дальше, по умолчанию окажется игроку закрыто, и открыть придётся отдельным
 * решением, а не забыв про него.
 */
const PLAYER_CAPABILITIES: readonly Capability[] = ['stats.view'];

/** Что разрешено каждому уровню. */
export const CAPABILITIES_BY_LEVEL: Readonly<Record<AccessLevel, readonly Capability[]>> = {
  player: PLAYER_CAPABILITIES,
  moderator: MODERATOR_CAPABILITIES,
  admin: FULL_CAPABILITIES,
  owner: FULL_CAPABILITIES,
};

export function hasCapability(level: AccessLevel, capability: Capability): boolean {
  return CAPABILITIES_BY_LEVEL[level].includes(capability);
}

/** Человекочитаемое название уровня. */
export const ACCESS_LEVEL_LABEL: Readonly<Record<AccessLevel, string>> = {
  player: 'Игрок',
  moderator: 'Модератор',
  admin: 'Администратор',
  owner: 'Владелец',
};

/** Данные вошедшего пользователя, которые видит фронт. */
export interface SessionUser {
  userId: Snowflake;
  /** Имя пользователя Discord. */
  username: string;
  /** Ник на сервере, если он задан. */
  displayName: string;
  avatarUrl: string | null;
  level: AccessLevel;
  capabilities: readonly Capability[];
}

/** Пользователи, которым разрешено добавлять бота на новые серверы. */
export const ALLOWED_INVITER_IDS: readonly string[] = [
  '1430248939017474094',
  '1535280782028050452',
];
